import React, { useState } from 'react';
import { Input, Button, Modal, Spin, InputNumber, Tabs, Table, message } from 'antd';
import { DeleteOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePWC201F1Form } from './hooks/usePWC201F1Form';
import { pwc201f1Api } from '../../services/pwc201f1Api';
import LookupField from '../../components/LookupField';

const PWC201F1Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    quoteDate, setQuoteDate, validDate, setValidDate,
    custCode, setCustCode, salesRep, setSalesRep,
    currency, setCurrency, exchRate, setExchRate,
    custAddr, setCustAddr, yourRef, setYourRef,
    remark, setRemark, remarkCode, setRemarkCode,
    deptCode, setDeptCode,
    details, addDetailLine, removeDetailLine, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = usePWC201F1Form();

  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [converting, setConverting] = useState(false);

  const handleConvertToSC = () => {
    if (!current) return;
    Modal.confirm({
      title: '轉為銷售合約',
      content: `確定要將報價單「${current.fc100_01}」轉為銷售合約 (S/C)？`,
      okText: '確定轉單', cancelText: '取消',
      onOk: async () => {
        setConverting(true);
        try {
          const scData = await pwc201f1Api.convertToSC(current.fc100_01);
          message.success('報價資料已帶入，請完成銷售合約填寫');
          navigate('/sc', { state: { importData: scData } });
        } catch (e: any) {
          message.error(e?.response?.data?.error || '轉單失敗');
        } finally {
          setConverting(false);
        }
      },
    });
  };

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除報價單「${current.fc100_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSearch = () => { if (searchText.trim()) quickSearch(searchText.trim()); };
  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  const detailColumns = [
    { title: '#', width: 40, render: (_: any, __: any, i: number) => i + 1 },
    { title: '產品代號1', dataIndex: 'fc101_11', width: 90, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={6} onChange={e => updateDetail(i, 'fc101_11', e.target.value)} /> : v },
    { title: '產品代號2', dataIndex: 'fc101_12', width: 90, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={6} onChange={e => updateDetail(i, 'fc101_12', e.target.value)} /> : v },
    { title: '預估成本', dataIndex: 'fc101_13', width: 100, render: (v: number, _: any, i: number) =>
      isEditing ? <InputNumber size="small" value={v} onChange={val => updateDetail(i, 'fc101_13', val || 0)} style={{width:'100%'}} /> : v?.toFixed(2) },
    { title: '費用率%', dataIndex: 'fc101_14', width: 80, render: (v: number, _: any, i: number) =>
      isEditing ? <InputNumber size="small" value={v} onChange={val => updateDetail(i, 'fc101_14', val || 0)} style={{width:'100%'}} /> : v },
    { title: '毛利率%', dataIndex: 'fc101_15', width: 80, render: (v: number, _: any, i: number) =>
      isEditing ? <InputNumber size="small" value={v} onChange={val => updateDetail(i, 'fc101_15', val || 0)} style={{width:'100%'}} /> : v },
    { title: '售價', dataIndex: 'fc101_16', width: 100, render: (v: number, _: any, i: number) =>
      isEditing ? <InputNumber size="small" value={v} onChange={val => updateDetail(i, 'fc101_16', val || 0)} style={{width:'100%'}} /> : v?.toFixed(2) },
    { title: '數量', dataIndex: 'fc101_17', width: 80, render: (v: number, _: any, i: number) =>
      isEditing ? <InputNumber size="small" value={v} onChange={val => updateDetail(i, 'fc101_17', val || 0)} style={{width:'100%'}} /> : v },
    { title: '單位', dataIndex: 'fc101_18', width: 60, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={3} onChange={e => updateDetail(i, 'fc101_18', e.target.value)} /> : v },
    { title: '金額', dataIndex: 'fc101_19', width: 100, render: (v: number) => v?.toFixed(2) },
    { title: '付款條件', dataIndex: 'fc101_21', width: 70, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={2} onChange={e => updateDetail(i, 'fc101_21', e.target.value)} /> : v },
    { title: '付款條件說明', dataIndex: 'fc101_211', width: 120, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={60} onChange={e => updateDetail(i, 'fc101_211', e.target.value)} /> : v },
    { title: '交易條件', dataIndex: 'fc101_22', width: 70, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={2} onChange={e => updateDetail(i, 'fc101_22', e.target.value)} /> : v },
    { title: '交易條件說明', dataIndex: 'fc101_221', width: 120, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={60} onChange={e => updateDetail(i, 'fc101_221', e.target.value)} /> : v },
    { title: '交期說明', dataIndex: 'fc101_23', width: 70, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={2} onChange={e => updateDetail(i, 'fc101_23', e.target.value)} /> : v },
    { title: '交期說明描述', dataIndex: 'fc101_231', width: 120, render: (v: string, _: any, i: number) =>
      isEditing ? <Input size="small" value={v} maxLength={60} onChange={e => updateDetail(i, 'fc101_231', e.target.value)} /> : v },
    { title: '產品規格', dataIndex: 'fc101_24', width: 150, render: (v: string, _: any, i: number) =>
      isEditing ? <Input.TextArea size="small" value={v} onChange={e => updateDetail(i, 'fc101_24', e.target.value)} autoSize={{minRows:1, maxRows:3}} /> : v },
    ...(isEditing ? [{ title: '', width: 40, render: (_: any, __: any, i: number) =>
      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeDetailLine(i)} /> }] : []),
  ];

  return (
    <Spin spinning={loading || converting}>
      <div className="max-w-5xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-3">
          報價單管理 (pwc201f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入單號/客戶/業務搜尋" value={searchText}
            onChange={e => setSearchText(e.target.value)} onPressEnter={handleSearch}
            disabled={isEditing} style={{ width: 300 }} />
          <Button onClick={handleSearch} disabled={isEditing}>查詢</Button>
        </div>

        <div className="flex items-center gap-1 mb-3">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
          {current && <span className="ml-2 text-sm text-gray-500">單號: {current.fc100_01}</span>}
        </div>

        <Tabs defaultActiveKey="1" items={[
          { key: '1', label: '基本資料', children: (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">報價日期 <span className="text-red-500">*</span></label>
                <Input type="date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">報價有效日期 <span className="text-red-500">*</span></label>
                <Input type="date" value={validDate} onChange={e => setValidDate(e.target.value)} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">客戶代號</label>
                <LookupField lookupType="customer" value={custCode} onChange={setCustCode}
                  onCodeConfirm={setCustCode} maxLength={12} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">部門代號</label>
                <Input value={deptCode} disabled maxLength={4} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">業務代號 <span className="text-red-500">*</span></label>
                <LookupField lookupType="employee" value={salesRep} onChange={setSalesRep}
                  onCodeConfirm={setSalesRep} maxLength={8} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">YOUR REF.</label>
                <Input value={yourRef} onChange={e => setYourRef(e.target.value)} maxLength={15} disabled={!isEditing} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">幣別 <span className="text-red-500">*</span></label>
                <LookupField lookupType="currency" value={currency} onChange={setCurrency}
                  onCodeConfirm={setCurrency} maxLength={3} disabled={!isEditing} style={{width:100}} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">匯率</label>
                <InputNumber value={exchRate} disabled style={{width:150}} precision={4} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">客戶地址</label>
                <Input.TextArea value={custAddr} onChange={e => setCustAddr(e.target.value)} disabled={!isEditing} rows={2} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">備註代號</label>
                <LookupField lookupType="remarkTemplate" value={remarkCode} onChange={setRemarkCode}
                  onCodeConfirm={setRemarkCode} maxLength={2} disabled={!isEditing} style={{width:80}} />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">備註說明</label>
                <Input.TextArea value={remark} onChange={e => setRemark(e.target.value)} disabled={!isEditing} rows={3} />
              </div>
            </div>
          )},
          { key: '2', label: '產品明細', children: (
            <div>
              {isEditing && (
                <div className="mb-2">
                  <Button icon={<PlusOutlined />} onClick={addDetailLine} size="small">新增明細</Button>
                </div>
              )}
              <Table dataSource={details} columns={detailColumns} rowKey={(_, i) => String(i)}
                size="small" pagination={false} scroll={{ x: 1800 }} bordered />
              {current && mode === 'browse' && (
                <div className="mt-2 text-sm text-gray-500">
                  總項數: {current.fc100_20} | 總金額: {current.fc100_17?.toFixed(2)}
                </div>
              )}
            </div>
          )},
        ]} />

        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mt-3">
            建立人員: {current.fc100_91} | 建立日期: {current.fc100_25 || '-'} | 修改人員: {current.fc100_93} | 修改日期: {current.fc100_92 || '-'}
          </div>
        )}

        <div className="flex items-center gap-2 mt-4">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
              <Button icon={<SwapOutlined />} onClick={handleConvertToSC}
                disabled={!current || converting} loading={converting}>轉 S/C</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default PWC201F1Form;
